import React from "react";
import { ToolPill } from "./Bits";

/**
 * TeamCarousel — Grid of team-member cards.
 * Each member supports:
 *   - initials (always shown as fallback)
 *   - image    (optional — when provided, shown on top of the sage initials block)
 *   - name, role, bio, tools[]
 *
 * To swap in real photos later, just add `image: "<url>"` to the member object.
 */
export default function TeamCarousel({ members, testid = "team-carousel" }) {
  return (
    <div className="relative" data-testid={testid}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {members.map((m, i) => (
          <div
            key={i}
            className="aidify-card p-5 flex flex-col"
            data-reveal
            data-i={(i % 6) + 1}
            data-testid={`team-card-${i}`}
          >
            {/* Image / Initials placeholder — taller so a real photo fits later */}
            <div className="relative aidify-card-sage h-72 mb-5 overflow-hidden flex items-center justify-center">
              {m.image ? (
                <img
                  src={m.image}
                  alt={m.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <>
                  {/* Big initials as placeholder */}
                  <span className="font-display font-extrabold text-7xl md:text-8xl text-forest-600/90 tracking-tight">
                    {m.initials}
                  </span>
                  {/* Subtle decorative dots */}
                  <span className="absolute top-4 right-4 w-2 h-2 rounded-full bg-forest-600/30" />
                  <span className="absolute bottom-5 left-5 w-3 h-3 rounded-full bg-forest-600/15" />
                </>
              )}
              {/* Role tag in the corner */}
              <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase font-medium text-forest-600 bg-cream-100/85 px-2.5 py-1 rounded-full">
                {m.role}
              </span>
            </div>

            <div className="flex items-baseline justify-between gap-3">
              <h4 className="font-display font-bold text-xl text-forest-600">{m.name}</h4>
            </div>
            <p className="text-sm text-forest-400 mt-2 leading-relaxed">{m.bio}</p>

            {m.tools?.length ? (
              <div className="flex flex-wrap gap-1.5 mt-5 pt-4 border-t border-forest-500/10">
                {m.tools.map((t, j) => (
                  <ToolPill key={j}>{t}</ToolPill>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
